import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Typography, Tag, Statistic, Timeline, Avatar, Space, Button,
  Empty, Spin, Divider, Progress, Input, Select,
} from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, EnvironmentOutlined, ClockCircleOutlined,
  LoginOutlined, LogoutOutlined, SearchOutlined, RiseOutlined, NumberOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { getDelegateJourney, searchDelegates, getEvents } from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const fmtMins = (m) => {
  if (!m || m < 1) return '< 1 min';
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return `${h}h ${mm}m`;
};

export default function DelegateJourney() {
  const navigate = useNavigate();
  const { delegateId } = useParams();
  const [searchParams] = useSearchParams();
  const queryEventId = searchParams.get('event_id');

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents]   = useState([]);
  const [eventId, setEventId] = useState(queryEventId || null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Load events once
  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      if (!eventId) {
        const active = ev.find(e => e.status === 'active') || ev[0];
        if (active) setEventId(active.id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const loadJourney = useCallback(() => {
    if (!delegateId) return;
    setLoading(true);
    getDelegateJourney(delegateId, eventId ? { event_id: eventId } : {})
      .then(r => setData(r.data.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [delegateId, eventId]);

  useEffect(() => { loadJourney(); }, [loadJourney]);

  // Live search for delegates (sidebar)
  useEffect(() => {
    if (searchQ.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      searchDelegates({ q: searchQ, event_id: eventId })
        .then(r => setSearchResults(r.data.data || []))
        .catch(() => setSearchResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, eventId]);

  if (!delegateId) {
    return (
      <div>
        <Row align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <Title level={4} style={{ margin: 0, color: PRIMARY }}>
              <UserOutlined style={{ marginRight: 8 }} />Delegate Journey
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Search a delegate to view their full movement timeline</Text>
          </Col>
        </Row>
        <Card style={{ borderRadius: 10, maxWidth: 600 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Select
              value={eventId}
              onChange={setEventId}
              style={{ width: '100%' }}
              placeholder="Select Event"
              options={events.map(e => ({ label: e.event_name, value: e.id }))}
            />
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name, RFID tag or company"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              size="large"
            />
            {searchResults.map(d => (
              <div key={d.id}
                onClick={() => navigate(`/delegate-journey/${d.id}${eventId ? `?event_id=${eventId}` : ''}`)}
                style={{
                  padding: 12, border: '1px solid #f0f0f0', borderRadius: 8,
                  cursor: 'pointer', background: '#fafafa',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <Space direction="vertical" size={0}>
                  <Text strong>{d.delegate_name}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {d.company || '-'} • <Text code style={{ fontSize: 10 }}>{d.rfid_tag}</Text>
                  </Text>
                </Space>
                <Space>
                  {d.category && <Tag color={d.category_color}>{d.category}</Tag>}
                  {d.current_location && <Tag color="green">📍 {d.current_location}</Tag>}
                </Space>
              </div>
            ))}
          </Space>
        </Card>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  if (!data) return <Empty description="Delegate not found" />;

  const { delegate, movements, summary, dwell_per_location } = data;

  return (
    <div>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
            <Title level={4} style={{ margin: 0, color: PRIMARY }}>
              <UserOutlined style={{ marginRight: 8 }} />Delegate Journey
            </Title>
          </Space>
        </Col>
      </Row>

      {/* Delegate profile card */}
      <Card style={{ borderRadius: 10, marginBottom: 16, background: `linear-gradient(135deg, ${PRIMARY} 0%, ${TEAL} 100%)` }}>
        <Row align="middle" gutter={16}>
          <Col>
            <Avatar size={70} style={{ background: '#fff', color: PRIMARY, fontWeight: 700, fontSize: 26 }}>
              {(delegate.delegate_name || '?').charAt(0).toUpperCase()}
            </Avatar>
          </Col>
          <Col flex={1}>
            <Title level={3} style={{ color: '#fff', margin: 0 }}>{delegate.delegate_name}</Title>
            <Space wrap style={{ marginTop: 4 }}>
              {delegate.category_name && (
                <Tag color={delegate.category_color || 'blue'}>{delegate.category_name}</Tag>
              )}
              <Tag style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}>
                <NumberOutlined /> {delegate.rfid_tag}
              </Tag>
              {delegate.company && (
                <Tag style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}>
                  🏢 {delegate.company}
                </Tag>
              )}
              {delegate.event_name && (
                <Tag style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}>
                  📅 {delegate.event_name}
                </Tag>
              )}
              <Tag color={delegate.current_location ? 'green' : 'default'}>
                {delegate.current_location ? `📍 ${delegate.current_location}` : '⚪ Not on premises'}
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* KPI cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Total Scans" value={summary?.total_scans || 0} prefix={<NumberOutlined />} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Zones Visited" value={summary?.unique_locations || 0} prefix={<EnvironmentOutlined />} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #10b981` }}>
            <Statistic title="First Seen" value={summary?.first_seen ? dayjs(summary.first_seen).format('DD MMM HH:mm') : '-'}
              prefix={<LoginOutlined />} valueStyle={{ color: '#10b981', fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #f59e0b` }}>
            <Statistic title="Last Seen" value={summary?.last_seen ? dayjs(summary.last_seen).format('DD MMM HH:mm') : '-'}
              prefix={<LogoutOutlined />} valueStyle={{ color: '#f59e0b', fontSize: 18 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Timeline */}
        <Col xs={24} lg={14}>
          <Card title={<Space><ClockCircleOutlined style={{ color: TEAL }} /> Movement Timeline ({movements?.length || 0} moves)</Space>}
            style={{ borderRadius: 10 }}>
            {!movements || movements.length === 0 ? (
              <Empty description="No movements recorded yet" />
            ) : (
              <Timeline
                mode="left"
                items={movements.map((m, i) => ({
                  dot: m.direction === 'Exit'
                    ? <LogoutOutlined style={{ color: '#ef4444', fontSize: 14 }} />
                    : <LoginOutlined style={{ color: '#10b981', fontSize: 14 }} />,
                  color: m.direction === 'Exit' ? 'red' : 'green',
                  label: <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>{dayjs(m.moved_at).format('DD MMM HH:mm:ss')}</Text>,
                  children: (
                    <div style={{ paddingBottom: 6 }}>
                      <Space wrap>
                        {m.from_name && <Tag>{m.from_name}</Tag>}
                        {m.from_name && m.to_name && <Text type="secondary">→</Text>}
                        {m.to_name
                          ? <Tag color={m.direction === 'Exit' ? 'red' : 'cyan'}>{m.to_name}</Tag>
                          : <Tag color="default">Exit (left building)</Tag>
                        }
                      </Space>
                      {m.reader_name && (
                        <div><Text type="secondary" style={{ fontSize: 11 }}>📡 {m.reader_name}</Text></div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </Card>
        </Col>

        {/* Time per location */}
        <Col xs={24} lg={10}>
          <Card title={<Space><ClockCircleOutlined style={{ color: PRIMARY }} /> Time Spent per Zone</Space>} style={{ borderRadius: 10 }}>
            {!dwell_per_location || dwell_per_location.length === 0 ? (
              <Empty description="No dwell data yet" />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={Math.max(220, dwell_per_location.length * 38)}>
                  <BarChart data={dwell_per_location} layout="vertical" margin={{ top: 8, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="location_name" tick={{ fontSize: 11 }} width={120} />
                    <RTooltip formatter={v => fmtMins(v)} />
                    <Bar dataKey="minutes" fill={TEAL} radius={[0, 4, 4, 0]} name="Time" />
                  </BarChart>
                </ResponsiveContainer>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  {dwell_per_location.map(d => (
                    <div key={d.location_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                      <Text>{d.location_name}</Text>
                      <Text strong style={{ color: PRIMARY }}>{fmtMins(d.minutes)}</Text>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
